import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ViewChild,
  inject,
  signal
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';
import { DefaultListConfig } from '@shared/modules/list/configs/default-list.config';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { ListConfig } from '@shared/modules/list/interfaces/list-config.interface';
import { MyWorkListConfig } from '@modules/my-work/shared/configs/my-work-list.config';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { MyWorkFiltersComponent } from '@modules/my-work/pages/my-work/components/my-work-filters/my-work-filters.component';
import { ListService } from '@shared/modules/list/services/list.service';
import { Config } from '@shared/configs/config';
import { ActivatedRoute, Router } from '@angular/router';
import { MyWorkService } from '@modules/my-work/shared/services/my-work.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ButtonGroupConfig } from '@shared/modules/ui/components/button-group-v2/button-group-v2.component';
import { ProjectTaskAPIService } from '@modules/projects/shared/services/project-task-api.service';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { StorageService } from '@core/services/storage.service';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { TaskType } from '@shared/enums/task-type.enum';
import { ListEventType } from '@shared/modules/list/model/list-event.model';
import { ListTasksGroupOrderEvent } from '@shared/modules/list/components/list-tasks/list-tasks.component';
import {
  ListTasksGroupEditEvent,
  ListTasksTaskDropEvent,
  ListTasksTaskStatusEvent
} from '@shared/modules/list/components/list-tasks/components/list-tasks-group/list-tasks-group.component';
import { ListGroupTask } from '@shared/modules/list/model/list-group-task.model';
import { Task } from '@shared/models/task.model';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { TaskGroup } from '@shared/models/task-group.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, forkJoin, of, switchMap, tap } from 'rxjs';
import { TaskController } from '@shared/controllers/task.controller';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { SnackBarService } from '@core/services/snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { TaskGroupModalComponent } from '@shared/modules/task-sidenav/components/task-group-modal/task-group-modal.component';
import { ETaskTypeGroup } from '@shared/modules/task-sidenav/enums/task-group.enum';

@Component({
  selector: 'my-work',
  templateUrl: './my-work.component.html',
  styleUrls: ['./my-work.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyWorkComponent implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly taskService = inject(TaskService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly dialog = inject(MatDialog);

  ButtonSize = ButtonSize;
  ButtonTypes = ButtonTypes;
  MyWorkFiltersComponent = MyWorkFiltersComponent;
  taskSidenavCtrl: TaskSidenavController;
  config: ListConfig;
  tabs: any[] = [];
  @ViewChild('selectTab') selectTab: NgSelectComponent;

  buttonGroupConfig: ButtonGroupConfig = {
    buttons: [
      {
        name: this.t.instant('MyWork.SideNavigation.myTasks'),
        prop: 'my',
        active: false
      },
      {
        name: this.t.instant('MyWork.SideNavigation.delegatedTasks'),
        prop: 'delegated',
        active: false
      }
    ]
  };

  readonly isDelegatedTasksTab$ = this.activatedRoute.queryParamMap.pipe(
    startWith(this.activatedRoute.snapshot.queryParamMap),
    map((p) => p.get('delegatedTasks') === '1'),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  readonly delegatedTasksMode = signal(false);
  readonly loading = signal(false);
  readonly initialLoading = signal(true);
  readonly projectGroups = signal<ListGroupTask[]>([]);
  readonly withoutProjectGroups = signal<ListGroupTask[]>([]);

  constructor(
    public t: TranslateService,
    private listService: ListService,
    private route: ActivatedRoute,
    private router: Router,
    public myWorkService: MyWorkService,
    private projectTaskApiService: ProjectTaskAPIService,
    private activatedRoute: ActivatedRoute,
    private store: StorageService,
    public listTaskService: ListTaskService
  ) {
    this.taskSidenavCtrl = new TaskSidenavController();
    this.initListConfig();
    this.listService.clearService();
    this.listService.config = this.config;
    this.listService.config.limit = this.listService.PerPage as number;
    this.listService.setParams('type', TaskType.TYPE_TASK);
    this.listTaskService.isMyWorkTaskLists = true;

    this.listenListServiceEvents();
    this.listenListTaskEvents();
    this.listenDelegatedTab();
  }

  ngAfterViewInit() {
    this.initActiveTab();
    this.listService.readUrlParams();
    this.listService.setInitSorting();
    this.listService.getRows();
  }

  createNewTask(): void {
    const userEmployeeId = this.store.UserPerson?.employees?.[0]?.id;
    this.taskSidenavCtrl.addTask({
      assignToIds: userEmployeeId ? [userEmployeeId] : []
    });
  }

  initListConfig() {
    this.config = cloneDeep(DefaultListConfig);
    this.config = Object.assign(this.config, MyWorkListConfig);
  }

  initActiveTab() {
    const updatedButtons = this.buttonGroupConfig.buttons.map((button, index) => {
      if (Number(this.route?.snapshot?.queryParams?.delegatedTasks) === 1) {
        return { ...button, active: index === 1 };
      } else {
        return { ...button, active: index === 0 };
      }
    });

    this.buttonGroupConfig = {
      ...this.buttonGroupConfig,
      buttons: updatedButtons
    };

    if (Number(this.route?.snapshot?.queryParams?.delegatedTasks) === 1) {
      this.selectTab.writeValue('delegated');
      this.listService.setDelegatedTasks(1);
    } else {
      this.selectTab.writeValue('my');
      this.listService.setDelegatedTasks(0);
    }
  }

  onTabClick(prop) {
    this.buttonGroupConfig.buttons.map((t) => {
      t.active = t.prop === prop;
    });

    this.selectTab.writeValue(prop);

    if (prop === 'delegated') {
      this.delegatedTasksMode.set(true);
      this.listService.setDelegatedTasks(1);
      this.listService.removeFilterByKey('[creator][in][]', false);
      this.router.navigate([], {
        queryParams: { delegatedTasks: 1 },
        queryParamsHandling: 'merge'
      });
    } else {
      this.delegatedTasksMode.set(false);
      this.listService.setDelegatedTasks(0);
      this.listService.removeFilterByKey('[taskEmployee-employee][in][]', false);
      this.router.navigate([], {
        queryParams: { delegatedTasks: 0 },
        queryParamsHandling: 'merge'
      });
    }

    this.listService.getRows();
  }

  private listenListServiceEvents() {
    this.listService.eventEmitter
      .pipe(
        tap(({ type }) => {
          if (type === ListEventType.START_GET_ROWS) {
            this.loading.set(true);
            return;
          }

          if (type === ListEventType.END_GET_ROWS) {
            this.hydrateGroupsFromRows();
            this.loading.set(false);
            if (this.initialLoading()) {
              this.initialLoading.set(false);
            }
            this.cdr.markForCheck();
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private listenListTaskEvents(): void {
    this.listTaskService.emitter
      .pipe(
        tap((event) => {
          switch (event.type) {
            case ListTaskEventType.ADD_TASK:
              this.handleTaskAdded(event.data as Task);
              break;
            case ListTaskEventType.ADD_SUB_TASK:
              this.handleTaskAdded(event.data as Task);
              break;
            case ListTaskEventType.ADD_TASK_GROUP:
              this.listService.getRows();
              break;
            case ListTaskEventType.UPDATE_TASK:
              this.handleTaskUpdated(event.data?.task as Task);
              break;
            case ListTaskEventType.REMOVE_TASK:
              this.handleTaskRemoved(event.data?.task as Task);
              break;
            case ListTaskEventType.UPDATE_TASK_LIST:
              this.listService.getRows();
              break;
            default:
              break;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.destroyRef.onDestroy(() => {
      MyWorkListConfig.url = `${Config.API}/task-list/my-task?delegatedTasks=0`;
      this.listTaskService.isMyWorkTaskLists = false;
    });
  }

  private listenDelegatedTab(): void {
    this.isDelegatedTasksTab$
      .pipe(
        tap((isDelegated) => this.delegatedTasksMode.set(isDelegated)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private hydrateGroupsFromRows(): void {
    const openedTaskIds = this.collectOpenedTaskIds([...this.projectGroups(), ...this.withoutProjectGroups()]);
    const rows = Array.isArray(this.listService.rows) ? this.listService.rows : [];
    const groups = this.normalizeMyWorkRows(rows, openedTaskIds);
    this.projectGroups.set(groups.filter((group) => !!group.withProject));
    this.withoutProjectGroups.set(groups.filter((group) => !group.withProject));
  }

  private normalizeMyWorkRows(rows: any[], openedTaskIds: Set<number>): ListGroupTask[] {
    const sourceRows = rows || [];
    const isNestedResponse = sourceRows.some((row) => Array.isArray(row?.data));
    const flatRows = isNestedResponse ? this.listTaskService.prepareGroupTasksForMyWork(sourceRows) : sourceRows;

    return (flatRows || [])
      .filter((row) => row?.taskGroup?.name !== ETaskTypeGroup.GROUP_CHANGE)
      .map((row) => {
        const taskGroup = new TaskGroup(row.taskGroup);
        const withProject = row?.withProject ?? taskGroup?.type === 'project';

        const tasks = this.sortMyWorkTasks(
          (row.tasks || []).map((task) => this.normalizeTask(task, taskGroup, openedTaskIds))
        );
        const completedTasks = this.sortMyWorkTasks(
          (row.completedTasks || []).map((task) => this.normalizeTask(task, taskGroup, openedTaskIds))
        );
        const cancelledTasks = this.sortMyWorkTasks(
          (row.cancelledTasks || []).map((task) => this.normalizeTask(task, taskGroup, openedTaskIds))
        );

        return {
          ...row,
          taskGroup,
          tasks,
          completedTasks,
          cancelledTasks,
          isMyWorkGroup: true,
          withProject
        } as ListGroupTask;
      })
      .sort((a, b) => (a?.taskGroup?.sortOrder || 0) - (b?.taskGroup?.sortOrder || 0));
  }

  private sortMyWorkTasks(tasks: Task[]): Task[] {
    const sortedByOrder = [...tasks].sort(
      (a, b) => (a?.workspaceTaskGroupOrder ?? 0) - (b?.workspaceTaskGroupOrder ?? 0)
    );
    const highPriority = sortedByOrder.filter((task) => !!task?.isHighPriority);
    const regular = sortedByOrder.filter((task) => !task?.isHighPriority);
    return [...highPriority, ...regular];
  }

  private normalizeTask(task: Task, taskGroup: TaskGroup, openedTaskIds: Set<number>): Task {
    const children = ((task as any)?.children || [])
      .map((child: Task) => this.normalizeTask(child, taskGroup, openedTaskIds))
      .sort((a, b) => (a?.workspaceTaskGroupOrder ?? 0) - (b?.workspaceTaskGroupOrder ?? 0));

    const workspaceTaskGroup = new TaskGroup((task as any)?.workspaceTaskGroup || taskGroup);
    const normalizedTask = new Task({
      ...(task as any),
      taskGroup,
      workspaceTaskGroup,
      children
    } as any);

    const taskId = Number(normalizedTask?.id || 0);
    const visibleChildren = (children || []).filter((child) => !child?.deleted);
    normalizedTask.children = children;
    normalizedTask.childrenCount = visibleChildren.length;
    normalizedTask.completedChildrenCount = visibleChildren.filter(
      (child) => child?.status === TaskStatus.COMPLETED
    ).length;
    normalizedTask.openedSubtasks = taskId > 0 ? openedTaskIds.has(taskId) : !!normalizedTask.openedSubtasks;

    return normalizedTask;
  }

  private collectOpenedTaskIds(groups: ListGroupTask[]): Set<number> {
    const ids = new Set<number>();

    const collect = (task: Task): void => {
      const taskId = Number(task?.id || 0);
      if (taskId > 0 && !!task?.openedSubtasks) {
        ids.add(taskId);
      }
      (task?.children || []).forEach((child) => collect(child));
    };

    (groups || []).forEach((group) => {
      (group?.tasks || []).forEach((task) => collect(task));
      (group?.completedTasks || []).forEach((task) => collect(task));
      (group?.cancelledTasks || []).forEach((task) => collect(task));
    });

    return ids;
  }

  createMyWorkTaskGroup(type: 'project' | 'without_project'): void {
    const dialogRef = this.taskSidenavCtrl.addTaskGroup(null, TaskType.TYPE_TASK, type);

    dialogRef.componentInstance.onGroupAdd
      .pipe(
        tap(() => this.listService.getRows()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onTaskStatusChanged(event: ListTasksTaskStatusEvent): void {
    const ctrl = new TaskController(event.task);
    const applyStatusToSubtasks = event.applyStatusToSubtasks ?? event.subtasksCompleted;
    const completeSubtasks$ = this.syncSubtasksStatus(event.task, applyStatusToSubtasks);
    const changedToCompleted =
      event.task?.status === TaskStatus.COMPLETED ||
      event.task?.status === TaskStatus.CANCELLED ||
      event.task?.status === ('canceled' as any);

    completeSubtasks$
      .pipe(
        switchMap(() => ctrl.changeTaskStatus(event.task, true)),
        catchError(() => {
          this.snackBarService.error(this.t.instant('Tasks.errorStatusChange'));
          this.listService.getRows();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.snackBarService.success(this.getStatusChangedMessage(event.task));
        if (changedToCompleted) {
          setTimeout(() => {
            this.handleTaskRemoved(event.task);
          }, 250);
        } else {
          this.upsertTaskLocally(event.task);
        }
      });
  }

  private syncSubtasksStatus(task: Task, applyStatusToSubtasks?: boolean) {
    if (!applyStatusToSubtasks) return of(null);

    if (task?.status === TaskStatus.COMPLETED) {
      return new TaskController(task).completeSubtasks(task);
    }

    const subtasks = this.collectDescendantTasks(task);
    if (!subtasks.length) return of(null);

    return forkJoin(
      subtasks.map((subtask) => {
        const ctrl = new TaskController(subtask);
        return ctrl.changeTaskStatus(subtask, true);
      })
    );
  }

  private collectDescendantTasks(task: Task): Task[] {
    const descendants: Task[] = [];

    const collect = (parent: Task): void => {
      (parent?.children || []).forEach((child) => {
        descendants.push(child);
        collect(child);
      });
    };

    collect(task);
    return descendants;
  }

  onTaskDrop(event: ListTasksTaskDropEvent): void {
    const moved = this.moveTaskLocally(event);

    const moveGroup$ =
      event.sourceGroupId !== event.targetGroupId
        ? this.taskService.assignTaskToGroup(event.taskId, event.targetGroupId)
        : of(null);

    moveGroup$
      .pipe(
        switchMap(() => this.projectTaskApiService.workspaceTaskOrderChange(event.taskId, event.index + 1)),
        tap(() => this.snackBarService.success(this.t.instant('ProjectTasks.Task.successTaskOrderChange'))),
        catchError(() => {
          if (moved) {
            this.listService.getRows();
          }
          this.snackBarService.error(this.t.instant('ProjectTasks.Task.errorTaskOrderChange'));
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onProjectGroupOrderChanged(event: ListTasksGroupOrderEvent): void {
    this.persistWorkspaceGroupOrder(event.groups, this.projectGroups());
  }

  onWithoutProjectGroupOrderChanged(event: ListTasksGroupOrderEvent): void {
    this.persistWorkspaceGroupOrder(event.groups, this.withoutProjectGroups());
  }

  private persistWorkspaceGroupOrder(
    groupsOrder: Array<{ id: number; order: number }>,
    currentGroups: ListGroupTask[]
  ): void {
    const orderedIds = [...currentGroups]
      .sort((a, b) => (a?.taskGroup?.sortOrder || 0) - (b?.taskGroup?.sortOrder || 0))
      .map((group) => Number(group?.taskGroup?.id || 0))
      .filter((id) => id > 0);

    const destinationById = new Map(groupsOrder.map((group) => [group.id, group.order]));
    const changedGroup = orderedIds
      .map((id, index) => ({ id, previousOrder: index + 1, newOrder: destinationById.get(id) || index + 1 }))
      .find((item) => item.previousOrder !== item.newOrder);

    if (!changedGroup) return;

    this.projectTaskApiService
      .workspaceGroupOrderChange(changedGroup.id, changedGroup.newOrder)
      .pipe(
        tap(() => this.snackBarService.success(this.t.instant('ProjectTasks.Task.successTaskOrderChange'))),
        catchError(() => {
          this.snackBarService.error(this.t.instant('ProjectTasks.Task.errorTaskOrderChange'));
          this.listService.getRows();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onGroupEditRequested(event: ListTasksGroupEditEvent): void {
    const group = event.group?.taskGroup;
    if (!group?.id) return;

    const dialogRef = this.dialog.open(TaskGroupModalComponent, {
      width: Config.DEFAULT_MODAL_WIDTH,
      autoFocus: true,
      data: { notSendNewGroup: true, edit: true, taskGroup: group }
    });

    dialogRef.componentInstance.group.setValue(group.name);

    const editSub = dialogRef.componentInstance.onGroupAdd.subscribe((groupName: string) => {
      this.taskService
        .editWorkspaceTaskGroupName(groupName, Number(group.id))
        .pipe(
          catchError(() => of(null)),
          finalize(() => this.listService.getRows()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => editSub.unsubscribe());
    });

    const removeSub = dialogRef.componentInstance.onGroupRemove.subscribe((taskGroup: TaskGroup) => {
      this.projectTaskApiService
        .removeWorkspaceGroup(taskGroup.id)
        .pipe(
          catchError(() => of(null)),
          finalize(() => this.listService.getRows()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => removeSub.unsubscribe());
    });
  }

  onAddTaskToGroup(group: ListGroupTask): void {
    const userEmployeeId = this.store.UserPerson?.employees?.[0]?.id;
    this.taskSidenavCtrl.addTask({
      taskGroupId: Number(group?.taskGroup?.id),
      taskType: TaskType.TYPE_TASK,
      groupType: group?.taskGroup?.type || null,
      assignToIds: userEmployeeId ? [userEmployeeId] : []
    });
  }

  private getStatusChangedMessage(task: Task): string {
    const rawType = task?.type || TaskType.TYPE_TASK;
    const normalizedType = rawType === 'company-task' || rawType === 'client-task' ? 'task' : rawType;
    const actionKey = task?.status === TaskStatus.COMPLETED ? 'completed' : 'created';

    return this.t.instant('Tasks.successStatusChangeAction', {
      taskType: this.t.instant(`Tasks.TaskType.${normalizedType}`),
      action: this.t.instant(`Tasks.statusAction.${actionKey}`)
    });
  }

  private handleTaskAdded(task: Task): void {
    if (!task) return;
    this.upsertTaskLocally(task);
  }

  private handleTaskUpdated(task: Task): void {
    if (!task) return;
    this.upsertTaskLocally(task);
  }

  private handleTaskRemoved(task: Task): void {
    if (!task?.id) return;
    this.applyGroupsMutation((groups) => this.removeTaskFromGroups(groups, Number(task.id)));
  }

  private upsertTaskLocally(task: Task): void {
    this.applyGroupsMutation((groups) => {
      const openedIds = this.collectOpenedTaskIds(groups);
      this.removeTaskFromGroups(groups, Number(task.id));

      if (task?.parentId) {
        const parent = this.findTaskById(groups, Number(task.parentId));
        if (!parent) return false;

        const normalized = this.normalizeTask(task, parent.taskGroup as TaskGroup, openedIds);
        const children = parent.children || [];
        children.push(normalized);
        parent.children = children
          .filter((child) => Number(child?.id) !== Number(normalized.id) || child === normalized)
          .sort((a, b) => (a?.workspaceTaskGroupOrder ?? 0) - (b?.workspaceTaskGroupOrder ?? 0));
        parent.childrenCount = parent.children.filter((child) => !child?.deleted).length;
        parent.completedChildrenCount = parent.children.filter(
          (child) => child?.status === TaskStatus.COMPLETED && !child?.deleted
        ).length;
        return true;
      }

      const groupId = Number(task?.workspaceTaskGroup?.id || task?.taskGroup?.id || 0);
      const group = groups.find((item) => Number(item?.taskGroup?.id || 0) === groupId);
      if (!group) return false;

      const normalized = this.normalizeTask(task, group.taskGroup as TaskGroup, openedIds);
      const targetCollection = this.getTaskCollectionByStatus(group, normalized?.status);
      targetCollection.push(normalized);
      this.sortCollectionInPlace(targetCollection);
      return true;
    });
  }

  private moveTaskLocally(event: ListTasksTaskDropEvent): boolean {
    let changed = false;
    this.applyGroupsMutation((groups) => {
      const sourceGroup = groups.find((group) => Number(group?.taskGroup?.id || 0) === Number(event.sourceGroupId));
      const targetGroup = groups.find((group) => Number(group?.taskGroup?.id || 0) === Number(event.targetGroupId));
      if (!sourceGroup || !targetGroup) return false;

      const source = this.getTaskCollectionByStatus(sourceGroup, event.sourceStatus);
      const target = this.getTaskCollectionByStatus(targetGroup, event.targetStatus);
      const sourceIndex = source.findIndex((task) => Number(task?.id) === Number(event.taskId));
      if (sourceIndex < 0) return false;

      const [task] = source.splice(sourceIndex, 1);
      if (!task) return false;

      if (!task.workspaceTaskGroup) {
        (task as any).workspaceTaskGroup = new TaskGroup(targetGroup.taskGroup as TaskGroup);
      }
      task.workspaceTaskGroup.id = targetGroup.taskGroup.id;
      task.workspaceTaskGroupOrder = event.index + 1;

      const safeIndex = Math.max(0, Math.min(event.index, target.length));
      target.splice(safeIndex, 0, task);
      target.forEach((item, index) => {
        item.workspaceTaskGroupOrder = index + 1;
      });

      changed = true;
      return true;
    });

    return changed;
  }

  private applyGroupsMutation(mutator: (groups: ListGroupTask[]) => boolean): void {
    const groups = [...this.projectGroups(), ...this.withoutProjectGroups()];
    const changed = mutator(groups);
    if (!changed) return;

    const sorted = [...groups].sort((a, b) => (a?.taskGroup?.sortOrder || 0) - (b?.taskGroup?.sortOrder || 0));
    this.projectGroups.set(sorted.filter((group) => !!group.withProject));
    this.withoutProjectGroups.set(sorted.filter((group) => !group.withProject));
    this.cdr.markForCheck();
  }

  private removeTaskFromGroups(groups: ListGroupTask[], taskId: number): boolean {
    let removed = false;

    const cloneTaskWithChildren = (task: Task, children: Task[]): Task => {
      return new Task({
        ...task,
        children
      } as any);
    };

    const removeFromChildren = (children: Task[] = []): { children: Task[]; changed: boolean } => {
      let changed = false;
      const nextChildren: Task[] = [];

      children.forEach((child) => {
        if (Number(child?.id) === taskId) {
          removed = true;
          changed = true;
          return;
        }

        const nestedResult = removeFromChildren(child.children || []);
        if (nestedResult.changed) {
          changed = true;
          nextChildren.push(cloneTaskWithChildren(child, nestedResult.children));
          return;
        }

        nextChildren.push(child);
      });

      return { children: nextChildren, changed };
    };

    const updatedGroups = groups.map((group) => {
      const collections: Array<keyof Pick<ListGroupTask, 'tasks' | 'completedTasks' | 'cancelledTasks'>> = [
        'tasks',
        'completedTasks',
        'cancelledTasks'
      ];
      let groupChanged = false;
      const nextGroup = { ...group } as ListGroupTask;

      collections.forEach((collection) => {
        const before = group[collection] || [];
        const updated = before.reduce((acc: Task[], task) => {
          if (Number(task?.id) === taskId) {
            removed = true;
            groupChanged = true;
            return acc;
          }

          const nestedResult = removeFromChildren(task.children || []);
          if (nestedResult.changed) {
            groupChanged = true;
            acc.push(cloneTaskWithChildren(task, nestedResult.children));
            return acc;
          }

          acc.push(task);
          return acc;
        }, []);

        if (updated.length !== before.length) {
          removed = true;
        }
        nextGroup[collection] = updated as any;
      });

      if (!groupChanged) {
        return group;
      }

      nextGroup.currentAllCount = (nextGroup.tasks || []).length + (nextGroup.completedTasks || []).length;
      nextGroup.currentCompletedCount = (nextGroup.completedTasks || []).length;

      return nextGroup;
    });

    groups.splice(0, groups.length, ...updatedGroups);

    return removed;
  }

  private findTaskById(groups: ListGroupTask[], taskId: number): Task | null {
    const stack: Task[] = [];
    groups.forEach((group) => {
      stack.push(...(group.tasks || []), ...(group.completedTasks || []), ...(group.cancelledTasks || []));
    });

    while (stack.length) {
      const current = stack.shift();
      if (!current) continue;
      if (Number(current.id) === taskId) return current;
      if (current.children?.length) {
        stack.push(...current.children);
      }
    }
    return null;
  }

  private getTaskCollectionByStatus(
    group: ListGroupTask,
    status: TaskStatus | string
  ): Task[] {
    if (status === TaskStatus.COMPLETED || status === 'completed') {
      group.completedTasks = group.completedTasks || [];
      return group.completedTasks;
    }

    if (status === TaskStatus.CANCELLED || status === 'cancelled' || status === 'canceled') {
      group.cancelledTasks = group.cancelledTasks || [];
      return group.cancelledTasks;
    }

    group.tasks = group.tasks || [];
    return group.tasks;
  }

  private sortCollectionInPlace(tasks: Task[]): void {
    tasks.sort((a, b) => {
      if (!!a?.isHighPriority !== !!b?.isHighPriority) {
        return a?.isHighPriority ? -1 : 1;
      }
      return (a?.workspaceTaskGroupOrder ?? 0) - (b?.workspaceTaskGroupOrder ?? 0);
    });
  }
}
